// apps/provider/src/services/api/monthClose.js
// Month Close package + bank reconciliation (org-gated via
// organization.settings.month_close_enabled).
//
// Methods: getCloseArAsOf, getReconciliationMonitors, getBillingCloses, getBillingClose, createBillingClose,
//          patchBillingClose, getBankAccounts, createBankAccount, patchBankAccount,
//          getBankStatements, createBankStatement, patchBankStatement,
//          getBankDeposits, createBankDeposit, patchBankDeposit, deleteBankDeposit,
//          getBankRecCandidates
//
// close-ar-asof, reconciliation-monitors and bank-rec-candidates sit behind the reporting admission gate
// (Xano functions/reporting_admission) and answer 429 under load; those getters
// retry through retryOnBusy like the other heavy reporting pulls.
import { retryOnBusy } from '../../utils/retryOnBusy';

/**
 * @returns {Object} Month close domain methods (bound to `this` of the composed apiService)
 */
export function createMonthCloseMethods() {
  return {
    /**
     * AR as a balance at one instant (as_of, end of day UTC) on the expected basis.
     * Population = completed encounters with DOS in [dos_from, as_of]; cash and
     * denials counted only through as_of. This is the "ending AR" line of the
     * month-close rollforward (the AR Aging export is a DOS-population view with
     * lifetime cash, so it cannot be).
     *
     * Xano: GET /reporting/close-ar-asof
     * Xano file: reporting-close-ar-asof_GET.xs
     *
     * @param {Object} filters
     * @param {string} filters.as_of - YYYY-MM-DD
     * @param {string} filters.dos_from - YYYY-MM-DD (go-live DOS cutoff)
     * @param {string} [filters.location_ids] - comma-separated
     * @param {string} [filters.location_id]
     * @param {string} [filters.care_setting] - 'outpatient' | 'home_health'
     * @param {'payment'|'deposit'} [filters.date_basis] - payment (default): cash cut off by the remit date (ledger effective date); deposit: by the confirmed bank deposit date, then the remit's manual deposit_date, then the remit date (org setting cash_basis = deposit_date)
     * @returns {Promise<{encounters: Array<Object>, meta: Object}>}
     */
    getCloseArAsOf(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.as_of) queryParams.append('as_of', filters.as_of);
      if (filters.dos_from) queryParams.append('dos_from', filters.dos_from);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.care_setting) queryParams.append('care_setting', filters.care_setting);
      if (filters.date_basis) queryParams.append('date_basis', filters.date_basis);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/close-ar-asof${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
      }), {});
    },

    /**
     * The five standing reconciliation monitors for a close window (ids and
     * dollars only): m1 unattached remit money in the period, m2 remits
     * received but not fully posted (stalled), m3 claim stamp vs ledger
     * drift, m4 duplicate chains, m5 patient money not in the ledger. The
     * snapshot is stored on the close and gates Finalize; a missing snapshot
     * counts as nonzero.
     *
     * Xano: GET /billing/reconciliation-monitors
     * Xano file: billing_reconciliation-monitors_GET.xs
     *
     * @param {Object} filters
     * @param {string} filters.period_start - YYYY-MM-DD
     * @param {string} filters.period_end - YYYY-MM-DD
     * @param {string} [filters.dos_from] - YYYY-MM-DD go-live DOS cutoff (defaults to period_start server side)
     * @param {number} [filters.stall_days] - m2 age threshold in days (server default 14)
     * @returns {Promise<{m1: Object, m2: Object, m3: Object, m4: Object, m5: Object, window: Object, computed_at: string}>}
     */
    getReconciliationMonitors(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.period_start) queryParams.append('period_start', filters.period_start);
      if (filters.period_end) queryParams.append('period_end', filters.period_end);
      if (filters.dos_from) queryParams.append('dos_from', filters.dos_from);
      if (filters.stall_days) queryParams.append('stall_days', String(filters.stall_days));

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/billing/reconciliation-monitors${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
      }), {});
    },

    /**
     * List the organization's closes, newest period first.
     * Xano: GET /billing/closes  (billing_closes_GET.xs)
     * @returns {Promise<Array<Object>>}
     */
    async getBillingCloses() {
      return this.request('/billing/closes', { method: 'GET' }, 'getBillingCloses');
    },

    /**
     * Fetch one close with signed download URLs for its stored files.
     * Xano: GET /billing/close/{billing_close_id}  (billing_close_billing_close_id_GET.xs)
     * @param {string} closeId
     * @returns {Promise<Object>} close record + file_urls[]
     */
    async getBillingClose(closeId) {
      return this.request(`/billing/close/${closeId}`, { method: 'GET' }, 'getBillingClose');
    },

    /**
     * Create a draft close for a period (idempotent per org+period).
     * Xano: POST /billing/close  (billing_close_POST.xs)
     * @param {{period: string, period_start: string, period_end: string, dos_from: string, filters?: Object, totals?: Object, row_keys?: Object, notes?: string}} data
     * @returns {Promise<Object>}
     */
    async createBillingClose(data) {
      return this.request('/billing/close', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'createBillingClose');
    },

    /**
     * Patch a close. `action` is one of update | attach_file | finalize | reopen.
     * Xano: PATCH /billing/close/{billing_close_id}  (billing_close_billing_close_id_PATCH.xs)
     * @param {string} closeId
     * @param {{action: string, totals?: Object, row_keys?: Object, bank_rec?: Object, monitors?: Object|null, monitor_acknowledgment?: string, notes?: string, file_data?: string, file_name?: string}} data
     * @returns {Promise<Object>}
     */
    async patchBillingClose(closeId, data) {
      return this.request(`/billing/close/${closeId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'patchBillingClose');
    },

    /** Xano: GET /billing/bank-accounts  (billing_bank-accounts_GET.xs) */
    async getBankAccounts() {
      return this.request('/billing/bank-accounts', { method: 'GET' }, 'getBankAccounts');
    },

    /**
     * Xano: POST /billing/bank-account  (billing_bank-account_POST.xs)
     * @param {{name: string, bank_name?: string, account_last4?: string, care_setting_scope?: string, description_rules?: Array<Object>}} data
     */
    async createBankAccount(data) {
      return this.request('/billing/bank-account', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'createBankAccount');
    },

    /** Xano: PATCH /billing/bank-account/{bank_account_id}  (billing_bank-account_bank_account_id_PATCH.xs) */
    async patchBankAccount(accountId, data) {
      return this.request(`/billing/bank-account/${accountId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'patchBankAccount');
    },

    /**
     * Xano: GET /billing/bank-statements  (billing_bank-statements_GET.xs)
     * @param {string} accountId
     * @param {string} [period] - YYYY-MM
     */
    async getBankStatements(accountId, period) {
      const queryParams = new URLSearchParams();
      queryParams.append('bank_account_id', accountId);
      if (period) queryParams.append('period', period);
      return this.request(`/billing/bank-statements?${queryParams.toString()}`, { method: 'GET' }, 'getBankStatements');
    },

    /**
     * Create (or reuse) a statement and bulk-insert its deposit lines (dedupe_key skips re-imports).
     * Xano: POST /billing/bank-statement  (billing_bank-statement_POST.xs)
     * @param {{bank_account_id: string, period: string, statement_date?: string, beginning_balance?: number, ending_balance?: number, additions_count?: number, additions_total?: number, source?: string, file_data?: string, file_name?: string, deposits: Array<Object>}} data
     * @returns {Promise<{statement: Object, inserted_count: number, skipped_count: number, deposits: Array<Object>}>}
     */
    async createBankStatement(data) {
      return this.request('/billing/bank-statement', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'createBankStatement');
    },

    /** Xano: PATCH /billing/bank-statement/{bank_statement_id}  (billing_bank-statement_bank_statement_id_PATCH.xs) */
    async patchBankStatement(statementId, data) {
      return this.request(`/billing/bank-statement/${statementId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'patchBankStatement');
    },

    /** Xano: GET /billing/bank-deposits  (billing_bank-deposits_GET.xs) */
    async getBankDeposits(statementId) {
      const queryParams = new URLSearchParams();
      queryParams.append('bank_statement_id', statementId);
      return this.request(`/billing/bank-deposits?${queryParams.toString()}`, { method: 'GET' }, 'getBankDeposits');
    },

    /**
     * Add a manual deposit line to a statement.
     * Xano: POST /billing/bank-deposit  (billing_bank-deposit_POST.xs)
     * @param {{bank_statement_id: string, deposit_date: string, description: string, amount: number, serial?: string, category?: string, notes?: string}} data
     */
    async createBankDeposit(data) {
      return this.request('/billing/bank-deposit', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'createBankDeposit');
    },

    /**
     * Xano: PATCH /billing/bank-deposit/{bank_deposit_id}  (billing_bank-deposit_bank_deposit_id_PATCH.xs)
     * @param {string} depositId
     * @param {{category?: string, match_status?: string, matches?: Array<Object>, variance_amount?: number, variance_type?: string, notes?: string}} data
     */
    async patchBankDeposit(depositId, data) {
      return this.request(`/billing/bank-deposit/${depositId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'patchBankDeposit');
    },

    /** Manual lines only. Xano: DELETE /billing/bank-deposit/{bank_deposit_id} */
    async deleteBankDeposit(depositId) {
      return this.request(`/billing/bank-deposit/${depositId}`, { method: 'DELETE' }, 'deleteBankDeposit');
    },

    /**
     * Prism money that could explain a statement's deposits: ERAs (check grain),
     * Stripe payments, manual invoice payments in a widened window, plus the ids
     * already used by other deposits' matches.
     *
     * Xano: GET /billing/bank-rec-candidates  (billing_bank-rec-candidates_GET.xs)
     * @param {{bank_account_id: string, start: string, end: string}} filters - YYYY-MM-DD window
     * @returns {Promise<{window: Object, scope: string, eras: Array<Object>, stripe_payments: Array<Object>, manual_payments: Array<Object>, used: Object, generated_at: string}>}
     */
    getBankRecCandidates(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.bank_account_id) queryParams.append('bank_account_id', filters.bank_account_id);
      if (filters.start) queryParams.append('start', filters.start);
      if (filters.end) queryParams.append('end', filters.end);
      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/billing/bank-rec-candidates${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
      }), {});
    }
  };
}
